import {sendSuccessEventToMarketingTrackingTool} from '../../businessAnalysisUtils';
import {useCreatePortal, useSignUpTrackingEvents} from '../../hooks';
import {labels, errorMessages} from '../../translations';
import {BaseFormProps, SignupForm} from '../../types';
import {Translate} from '@planday-corp/globalization';
import {useService} from '@planday-corp/react-app';
import Button from '@planday-corp/ui/actions/Button';
import ProgressBar from '@planday-corp/ui/indicators/ProgressBar';
import Text from '@planday-corp/ui/text/Text';
import {useEffect, useState} from 'react';
import styled from 'styled-components';
import {AppContext} from '~/App';
import errorImagePath from '~/shared/images/error.svg';
import plandayLogo from '~/shared/images/plandayLogoWhite.svg';

const Container = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  align-self: center;
  margin: 0 auto;
  max-width: 25rem;
`;

const StyledProgressBar = styled(ProgressBar)`
  @media (min-width: 25rem) {
    width: 23rem;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledErrorImg = styled.img`
  display: block;
  max-width: 12.5rem;
  margin: 0 auto;
`;

const StyledLogo = styled.img`
  width: 12rem;
  height: auto;
  margin-bottom: 1rem;
`;

interface SignupScreenProps {
  form: SignupForm;
  setStep: BaseFormProps['setStep'];
}

export default function PortalCreationProgress(props: SignupScreenProps) {
  const {form, setStep} = props;
  const track = useSignUpTrackingEvents();
  const [isSimulationStarted, setIsSimulationStarted] = useState(false);
  const context = useService<AppContext>();
  const [showErrorView, setShowErrorView] = useState(false);

  const {status, error, createPortalResponse} = useCreatePortal(form, context);

  useEffect(() => {
    if (status === 'error' || error) {
      setShowErrorView(true);
    }
  });

  useEffect(() => {
    setIsSimulationStarted(true);
  }, []);

  useEffect(() => {
    if (status === 'success' && createPortalResponse?.autoLoginUrl) {
      const stringifiedPortalId = createPortalResponse.portalId
        ? createPortalResponse.portalId.toString()
        : '';
      track.portalReady(stringifiedPortalId);
      sendSuccessEventToMarketingTrackingTool(form.email);
      // Timeout to allow progress bar animation to finish smoothly
      setTimeout(() => {
        window.location.assign(
          createPortalResponse.autoLoginUrl?.toString() || ''
        );
      }, 1000);
    }
  }, [status, createPortalResponse]);

  return (
    <Container>
      <StyledLogo src={plandayLogo} />
      {showErrorView ? (
        <>
          <StyledErrorImg src={errorImagePath} />
          <Text size="title4" color="white">
            {errorMessages.LoadingError}
          </Text>
          <StyledButton
            name="previous"
            onClick={() => setStep('account')}
            type="primary"
          >
            <Translate {...labels.loadingErrorCTA} />
          </StyledButton>
        </>
      ) : (
        <>
          <Text size="title4" color="white">
            {labels.title}
          </Text>
          <StyledProgressBar
            mode="simulated"
            simulationType="dynamic"
            isSimulationStarted={isSimulationStarted}
            isSimulationFinished={status === 'success'}
            thickness="0.25rem"
          />
        </>
      )}
    </Container>
  );
}
